import {
  Anchor,
  Box,
  Button,
  Container,
  Paper,
  SegmentedControl,
  TextInput,
  Title,
  Alert,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { useState, useEffect } from 'react';
import { notifications } from '@mantine/notifications';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '../store/useAuthStore';
import { requestMagicLink } from '../services/userManager';

export default function Login() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [loggingIn, setLoggingIn] = useState<boolean>(false);

  // Check if user is already logged in and redirect if necessary
  useEffect(() => {
    const { isLoggedIn, isInitialized } = useAuthStore.getState();

    if (isInitialized && isLoggedIn) {
      navigate('/', { replace: true });
    }
  }, [navigate]);

  const form = useForm({
    mode: 'uncontrolled',
    initialValues: {
      email: '',
      termsOfService: false,
    },

    validate: {
      email: (value: string) =>
        /^\S+@\S+$/.test(value) ? null : t('invalid_email', 'Email non valida'),
    },
  });

  const onSubmit = async (values: any) => {
    setLoggingIn(true);

    try {
      const response = await requestMagicLink(values.email);

      notifications.show({
        color: 'teal',
        withBorder: true,
        radius: 'lg',
        title: t('email_sent', 'Email inviata'),
        autoClose: false,
        message: response.message,
      });
    } catch (error) {
      notifications.show({
        color: 'red',
        withBorder: true,
        radius: 'lg',
        title: 'An error occurred',
        autoClose: false,
        message:
          error instanceof Error ? error.message : 'Unknown error occurred',
      });
    } finally {
      setLoggingIn(false);
    }
  };

  return (
    <>
      <Alert color='blue' title='AVVISO IMPORTANTE AGLI UTENTI:'>
        <ul style={{ paddingLeft: '1rem', marginTop: '0.5rem' }}>
          <li>
            Se avete creato un account <strong>prima del 1 maggio 2025</strong>{' '}
            e avete già iniziato o concluso uno dei nostri corsi, il link di
            accesso ai vostri prodotti/servizi è{' '}
            <a
              href='https://app.become-hub.com'
              target='_blank'
              style={{ textDecoration: 'none' }}
            >
              app.become-hub.com
            </a>
          </li>
          <li>
            Se invece avete creato un account{' '}
            <strong>dopo il 1 maggio 2025</strong>, il link di accesso ai vostri
            prodotti/servizi è{' '}
            <a
              href='https://platform.become-hub.com'
              target='_blank'
              style={{ textDecoration: 'none' }}
            >
              platform.become-hub.com
            </a>
          </li>
          <li>
            Se non avete ancora creato un account, vi preghiamo di registrarvi
            qui sotto e successivamente accedere ai vostri prodotti/servizi su{' '}
            <a
              href='https://platform.become-hub.com'
              target='_blank'
              style={{ textDecoration: 'none' }}
            >
              platform.become-hub.com
            </a>
          </li>
        </ul>
        <p style={{ marginTop: '0.5rem' }}>
          Per informazioni contattate :
          <a
            href='mailto:support@become-hub.com'
            style={{ textDecoration: 'none' }}
          >
            support@become-hub.com
          </a>
        </p>
      </Alert>
      <Container size={420} my={50}>
        <Title ta='center'>{t('login_title', 'Accedi a BECOME')}</Title>
        <SegmentedControl
          mt={'md'}
          fullWidth
          onChange={() => navigate('/register')}
          data={[
            { label: t('login', 'Login'), value: 'login' },
            { label: t('create_account', 'Crea account'), value: 'register' },
          ]}
        />

        <Paper withBorder shadow='md' p={30} mt={30} radius='sm'>
          <form onSubmit={form.onSubmit(onSubmit)}>
            <Box style={{ position: 'relative' }}>
              <TextInput
                label='Email'
                disabled={loggingIn}
                key={form.key('email')}
                {...form.getInputProps('email')}
                placeholder='m.rossi@libero.it'
                required
              />
            </Box>
            <Button
              loading={loggingIn}
              disabled={loggingIn}
              fullWidth
              mt='xl'
              type='submit'
            >
              {t('login', 'Login')}
            </Button>
          </form>
          <Box ta='center' mt='md'>
            <Anchor
              component='button'
              type='button'
              size='sm'
              c='dimmed'
              underline='hover'
              onClick={() => navigate('/auth/login')}
            >
              Accedi con email e password
            </Anchor>
          </Box>
        </Paper>
      </Container>
    </>
  );
}
